import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { fetchPersonalDrives } from '../../../../api/businessAndEnterprise/driveApi';
import { IoChevronForward, IoChevronDown } from 'react-icons/io5';
import { FaFolder, FaFolderOpen } from 'react-icons/fa';

const FolderTreeItem = ({ folder, selectedFolder, onFolderSelect, searchTerm }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Auto-expand if search matches this folder or any subfolder
    useEffect(() => {
        if (searchTerm && folderMatchesSearch(folder, searchTerm)) {
            setIsOpen(true);
        }
    }, [searchTerm, folder]);

    const folderMatchesSearch = (folder, term) => {
        const lowerTerm = term.toLowerCase();
        if (folder.name.toLowerCase().includes(lowerTerm)) return true;
        return folder.subfolders?.some(sub => folderMatchesSearch(sub, term));
    };

    const shouldShow = !searchTerm || folderMatchesSearch(folder, searchTerm);

    if (!shouldShow) return null;

    const handleToggle = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleSelect = (e) => {
        e.stopPropagation();
        onFolderSelect(folder);
    };

    const isHighlighted = searchTerm && folder.name.toLowerCase().includes(searchTerm.toLowerCase());
    const isSelected = selectedFolder?.id === folder.id;

    return (
        <li className="list-group-item border-0 p-0">
            <div 
                className={`d-flex align-items-center py-2 px-3 rounded ${
                    isSelected ? 'bg-dark bg-opacity-10 border border-dark' : 'hover-bg-light'
                } ${isHighlighted ? 'bg-warning bg-opacity-10' : ''}`}
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={handleSelect}
            >
                {folder.subfolders && folder.subfolders.length > 0 ? (
                    <div 
                        onClick={handleToggle} 
                        style={{ width: '20px', marginRight: '8px' }}
                        className="d-flex align-items-center"
                    >
                        {isOpen ? <IoChevronDown size={16} /> : <IoChevronForward size={16} />}
                    </div>
                ) : (
                    <div style={{ width: '20px', marginRight: '8px' }}></div>
                )}
                {isOpen ? (
                    <FaFolderOpen size={18} className="me-2"  />
                ) : (
                    <FaFolder size={18} className="me-2"  />
                )}
                <span className={`flex-grow-1 ${isSelected ? 'fw-bold' : ''}`}>
                    {folder.name}
                </span>
            </div>
            {isOpen && folder.subfolders && folder.subfolders.length > 0 && (
                <ul className="list-group ms-4 mt-1">
                    {folder.subfolders.map((subfolder) => (
                        <FolderTreeItem
                            key={subfolder.id}
                            folder={subfolder}
                            selectedFolder={selectedFolder}
                            onFolderSelect={onFolderSelect}
                            searchTerm={searchTerm}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

const CopyModal = ({ show, onClose, onConfirm, onSelectFolder, currentFolderId }) => {
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (show) {
            setLoading(true);
            fetchPersonalDrives({}, (response) => {
                if (response?.status === true) {
                    const allFolders = response?.folders?.data || [];
                    const filteredFolders = allFolders.filter(folder => folder.id !== currentFolderId);
                    setFolders(filteredFolders);
                }
                setLoading(false);
            });
        } else {
            setSelectedFolder(null);
            setSearchTerm('');
        }
    }, [show, currentFolderId]);

    const handleFolderSelect = (folder) => {
        setSelectedFolder(folder);
        onSelectFolder(folder.id);
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Copy Folder</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-dark" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="mb-3">Select a destination folder:</p>
                        
                        {/* Search Box */}
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Search folders..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </Form.Group>

                        {/* Selected Folder Display */}
                        {selectedFolder && (
                            <div className="alert alert-dark py-2 px-3 mb-3 d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <small className="text-muted d-block">Selected destination:</small>
                                    <div className="fw-bold mt-1 d-flex align-items-center">
                                        <FaFolder size={18} className="me-2" />
                                        {selectedFolder.name}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Folder Tree */}
                        {folders.length > 0 ? (
                            <div 
                                style={{ 
                                    maxHeight: '400px', 
                                    overflowY: 'auto',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    padding: '0.5rem'
                                }}
                            >
                                <ul className="list-group list-group-flush">
                                    {folders.map((folder) => (
                                        <FolderTreeItem
                                            key={folder.id}
                                            folder={folder}
                                            selectedFolder={selectedFolder}
                                            onFolderSelect={handleFolderSelect}
                                            searchTerm={searchTerm}
                                        />
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="text-center text-muted py-5">
                                <FaFolder size={48} style={{ opacity: 0.3 }} />
                                <p className="mt-3 mb-0">No other folders available to copy to.</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-end gap-2">
                <Button 
                    style={{
                        backgroundColor: '#AEF468',
                        border: 'none',
                        color: 'black'
                    }}
                    onClick={onConfirm} 
                    disabled={!selectedFolder}
                >
                    Copy Here
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CopyModal;